import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import PlanSummary from "./components/PlanSummary/PlanSummaryContainer";
import ProcessPathTabs from "./components/ProcessPathTabs";
import Chart from "./components/ChartContainer";
import MetricsSection from "../../components/MetricsSection";
import ShiftTotalsSection from "../../components/ShiftTotalsSection/ShiftTotalsSectionContainer";
// V2 counterparts of the four review surfaces. Same store slice
// (reviewAndPublish.shiftPlanOutput) backs both generations, so these are
// drop-in — see the RCC_PUBLISHED_SHIFT_PLAN_V2 note on the component.
import ChartV2 from "./components/v2/Chart";
import MetricsSectionV2 from "../../components/MetricsSection/v2/MetricsSection";
import ShiftTotalsSectionV2 from "../../components/ShiftTotalsSection/v2/ShiftTotalsSectionContainer";
import ShiftDataTableV2 from "../../components/ShiftDataTable/v2/ShiftDataTableContainer";
import Footer from "../../components/Footer";
import { setNavigationEnabled } from "../../store/slices/navigationSlice";
import { buildGetShiftPlanOutputRequest, GetShiftPlanOutputRequest, GetShiftPlanOutputResponse, ShiftSummaryCategory } from "./model";
import "./index.scss";
import ShiftDataTable from "../../components/ShiftDataTable";
import RatesRosterLoader from "../../components/RatesRosterLoader/RatesRosterLoader";
import { POLLING_INTERVAL_IN_MS, MAX_RETRIES } from "./constants";
import { PlanStatus } from "../../utils/constants";
import {useAppDispatch, useAppSelector} from "../../store";
import {showError, showNotification} from "../../store/slices/errorSlice";
import {createShiftPlan, getClockWindowNameBasedOnSelectedOption, getPlanTypeBasedOnSelectedTab} from "../../services/shiftPlanService";
import { PlanVersion } from "../../types/shiftPlan";
import {setPlanId, setShiftPlanData, clearShiftPlanData} from "../../store/slices/shiftPlanSlice";
import {useNavigate} from "react-router-dom";
import { ROUTES } from "../../utils/constants";
import {
    setPostPublishView,
} from "../../store/slices/v2/publishedShiftPlanFeatureSlice";
import { useResolvePublishedShiftPlanV2 } from "../../hooks/useResolvePublishedShiftPlanV2";
import { useResolveMidShiftReplan } from "../../hooks/useResolveMidShiftReplan";
import {
  getVolumeTrend,
  splitVolumeSeries,
  withoutActualPoints,
} from "../../utils/midShiftActuals";

// Import sub-page components for review tabs
import RosterAndRates from "../rosterAndRates";
import VolumesAndWipContainer from "../volumesAndWip/VolumesAndWipContainer";
// Same read-only view the published-plan summary uses, so the Checkout
// adjustments an operator just made are reviewable before publish rather than
// only visible after it.
import CheckoutOverridesView from "../shiftPlanning/CurrentPlanSummary/v2/CheckoutOverridesView";

interface ReviewAndPublishStateProps {
    shiftPlanOutput?: GetShiftPlanOutputResponse;
    planId: string | null;
    date: string | null;
    shiftType: string | null;
    planType: string | null;
    stationCode: string | null;
}

interface ReviewAndPublishDispatchProps {
    getShiftPlanOutput: (getShiftPlanOutputRequest: GetShiftPlanOutputRequest) => void;
    publishShiftPlan: (planId: string, shiftType: string) => Promise<any>;
}

type Props = ReviewAndPublishStateProps & ReviewAndPublishDispatchProps;

/**
 * Review and Publish component
 */
const ReviewAndPublish: React.FC<Props> = ({
  shiftPlanOutput,
  planId,
  date,
  shiftType,
  planType,
  stationCode,
  getShiftPlanOutput,
  publishShiftPlan
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Tri-state RCC_PUBLISHED_SHIFT_PLAN_V2: null until resolved. This page renders
  // the same surfaces as the published-plan summary, so it follows the same flag —
  // one switch flips both, and a rollback can't leave the review step showing V2
  // charts while the summary shows V1. Optional-chained so it's safe if the slice
  // is absent.
  // Resolve the V2 flag (shared via Redux). The workflow normally enters through ShiftPlanning,
  // which already resolved it, but a deep link straight to /shift-plan/review relies on this.
  const publishedShiftPlanV2Flag = useResolvePublishedShiftPlanV2(stationCode);
  // Only record the post-publish pointer when V2 is enabled, so V1 is unaffected.
  const isPublishedShiftPlanV2Enabled = publishedShiftPlanV2Flag === true;
  // Mid-shift actual-vs-projected display on the V2 surfaces (RCC_MID_SHIFT_REPLAN, user OR site),
  // resolved into Redux so the chart and the Process Path table read the same flag. Fail closed.
  const showActuals = useResolveMidShiftReplan(stationCode);
  const volumeTrend = getVolumeTrend(shiftPlanOutput);
  const volumeSeries = useMemo(
    () => showActuals
      ? splitVolumeSeries(volumeTrend)
      : { actual: [], projected: withoutActualPoints(volumeTrend) },
    [showActuals, volumeTrend]
  );
  // ShiftPlanLayout resolves this before it renders any workflow route, so it is
  // already settled by the time the review step mounts.
  const isCheckoutEnabled = useAppSelector((state) => state.checkout?.isCheckoutEnabled) === true;
  const [planDataLoading, setPlanDataLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ShiftSummaryCategory | undefined>(undefined);
  const [activeReviewTab, setActiveReviewTab] = useState<string>("shift-plan");
  // Publish is now offered at both the top and the bottom of the page, so the
  // in-flight flag stops the same plan being published twice.
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  // The guard below reads a ref rather than `isPublishing`: clicking the top and
  // bottom Publish buttons within one React batch would have both handlers see the
  // pre-update state and publish the plan twice.
  const publishInFlightRef = useRef<boolean>(false);

  // Disable navigation when component mounts, enable when it unmounts
  useEffect(() => {
    dispatch(setNavigationEnabled(false));

    return () => {
      dispatch(setNavigationEnabled(true));
    };
  }, [dispatch]);

  // Start polling when component mounts and required data is available
  useEffect(() => {
    if (!planId || !date || !stationCode || !shiftType) {
      return;
    }

    // Reset state whenever input changes
    setRetryCount(0);
    setPlanDataLoading(true);

    // No "clear the old interval" guard here: React runs this effect's cleanup
    // (below) before every re-run, and that cleanup already clears the interval
    // and nulls the ref, so the ref is always null at this point.

    const fetchData = () => {
      getShiftPlanOutput(
          buildGetShiftPlanOutputRequest(planId!, date!, stationCode!, shiftType!)
      );
    };

    // First API call
    fetchData();

    // Start interval for subsequent calls
    const id: ReturnType<typeof setInterval> = setInterval(() => {
      setRetryCount(prev => {
        // If next retry would exceed max retries → stop polling
        if (prev + 1 >= MAX_RETRIES) {
          clearInterval(id);
          pollingIntervalRef.current = null;
          setPlanDataLoading(false);

          dispatch(showError({
            title: "Failed to Fetch Shift Plan Data",
            message: "Unable to fetch Shift Plan Data. Please try again later."
          }));

          return prev + 1;
        }

        // Continue polling
        fetchData();
        return prev + 1;
      });
    }, POLLING_INTERVAL_IN_MS);

    pollingIntervalRef.current = id;

    // Cleanup on unmount or dependency change
    return () => {
      clearInterval(id);
      pollingIntervalRef.current = null;
      setPlanDataLoading(false);
    };

  }, [planId, date, stationCode, shiftType]);

  useEffect(() => {
    if (shiftPlanOutput?.planStatus === PlanStatus.PENDING_APPROVAL) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      setPlanDataLoading(false);
    }
  }, [shiftPlanOutput]);

  // Update selected category when shift plan data changes
  useEffect(() => {
    if (shiftPlanOutput?.shiftSummary && shiftPlanOutput.shiftSummary.length > 0) {
      // Set default to first category if no category is selected or current selection is not available
      const currentCategoryExists = selectedCategory && 
        shiftPlanOutput.shiftSummary.some(cat => cat.heading === selectedCategory.heading);
      
      if (!currentCategoryExists) {
        setSelectedCategory(shiftPlanOutput.shiftSummary[0]);
      }
    }
  }, [shiftPlanOutput?.shiftSummary, selectedCategory]);

  // Handle tab change from ProcessPathTabs
  const handleTabChange = useCallback((categoryId: string) => {
    if (shiftPlanOutput?.shiftSummary) {
      const category = shiftPlanOutput.shiftSummary.find(cat => cat.heading === categoryId);
      if (category) {
        setSelectedCategory(category);
      }
    }
  }, [shiftPlanOutput?.shiftSummary]);



  // Handle publish
  const handlePublish = async () => {
    if (publishInFlightRef.current) {
      return;
    }
    try {
      if (!planId) {
        dispatch(showError({
          title: "Publishing Error",
          message: "Plan ID is required to publish."
        }));
        return;
      }

      publishInFlightRef.current = true;
      setIsPublishing(true);
      const result = await publishShiftPlan(planId, getClockWindowNameBasedOnSelectedOption(shiftType!));

      // Access the response from the thunk's payload
      const response = result.payload;

      if (response && response.status === PlanStatus.APPROVED) {
        console.log("Plan published successfully")
        dispatch(showNotification({
          title: "Published Successfully",
          message: "Shift plan published successfully!",
          type: "success"
        }));
        
        // Enable navigation before navigating away
        dispatch(setNavigationEnabled(true));
        // Clear the shift plan workflow state so that subsequent visits to
        // /shift-plan/* correctly redirect to home instead of re-entering
        // the completed workflow with stale data.
        dispatch(clearShiftPlanData());

        // Point the home shift-plan summary at the plan just published so it loads
        // that shift and opens the V2 page on it (Operator Override view) instead of
        // resetting to today's default and showing a blank Override view
        // (UTR-REFLEX-1163). Only set it when V2 is enabled so V1 is unaffected; the
        // home host consumes and clears this one-shot pointer.
        if (isPublishedShiftPlanV2Enabled && planId && date && shiftType && planType) {
          dispatch(setPostPublishView({ planId, date, shift: shiftType, tab: planType }));
        }
        navigate(ROUTES.HOME);
      }
      
    } catch (error) {
      console.error("Error publishing shift plan:", error);
      dispatch(showError({
        title: "Publishing Failed",
        message: "Failed to publish shift plan. Please try again."
      }));
    } finally {
      publishInFlightRef.current = false;
      setIsPublishing(false);
    }
  };

  const onGenerateButtonClick = async () => {
    setIsGenerating(true);

    try {
      const response = await createShiftPlan(date!,
          getClockWindowNameBasedOnSelectedOption(shiftType!), getPlanTypeBasedOnSelectedTab(planType!), PlanVersion.STATIC_V1);

      if (response && response.status &&
            response.status !== 'FAILED' && response.planId) {
        // Dispatch Redux action with selected data

        dispatch(
            setShiftPlanData({
              date: date!,
              shift: shiftType!,
              plan: planType!
            })
        );


        // Set plan ID in Redux
        dispatch(
            setPlanId({
              planId: response.planId
            })
        );

        // Enable navigation before navigating away
        dispatch(setNavigationEnabled(true));

        // Navigate to the shift plan layout only after successful API call
        navigate(ROUTES.SHIFT_PLAN_ROSTER);
      } else {
        dispatch(showError({
          title: 'Generation Failed',
          message: response?.status === 'FAILED'
            ? 'Shift plan generation failed. Please try again.'
            : 'Unable to create shift plan. Please try again.'
        }));
      }
    } catch (error) {
      console.error('Error generating shift plan:', error);
      dispatch(showError({
        title: 'Generation Failed',
        message: 'Failed to generate shift plan. Please try again.'
      }));
    }

    setIsGenerating(false);
  }

  // Check if we should show full screen loader while polling for planStatus
  // Shows loader when: initial loading OR plan data loading and plan status is not PENDING_APPROVAL
  // Also hold the loader while the V2 flag is unresolved: mounting V1 surfaces and
  // swapping them for V2 once the flag lands would remount and refetch at the same
  // slot, so wait and mount the right generation once (same reasoning as
  // CurrentPlanSummaryGated). Instant locally, one facade round-trip when deployed.
  const shouldShowFullScreenLoader = publishedShiftPlanV2Flag === null
    || (planDataLoading && (!shiftPlanOutput || shiftPlanOutput.planStatus !== PlanStatus.PENDING_APPROVAL));
  const disableGenerateButton = !date || !shiftType || !planType;

  return (
    <Box spacingInset="300" className="review-and-publish">
      {shouldShowFullScreenLoader ? (
        <RatesRosterLoader status="loading" type={"plan"} message={"Loading Shift Plan data"}/>
      ) : shiftPlanOutput ? (
        <>
          <Box className="top-section">
            <Box className="header">
              <Box>
                <Heading type="h300" level={2}>
                  Review and Publish
                </Heading>
                <Text>Verify values below and confirm</Text>
              </Box>
            </Box>
            <PlanSummary
                onButtonClick={onGenerateButtonClick}
                buttonLabel={isGenerating ? "Generating..." : "Start New Shift Plan"}
                disableButton={disableGenerateButton}
            />
          </Box>

          {/* Publish is offered here as well as at the bottom: an operator who is
              already happy with the RCC-generated plan can publish without
              scrolling past every review tab. */}
          <Footer
            position="top"
            nextButtonDisplay={isPublishing ? "Publishing..." : "Publish"}
            onNext={handlePublish}
            showClearOverrides={false}
            disabled={isPublishing}
          />

          {/* Review tabs for navigating between input sections */}
          <Box className="review-tabs-container">
            <TabGroup
              fill="line"
              value={activeReviewTab}
              onChange={setActiveReviewTab}
              aria-labelledby="review-tabs"
            >
              <Tab value="shift-plan">Shift Plan</Tab>
              <Tab value="rates-roster">Rates & Roster</Tab>
              <Tab value="volume-buffer">Volume & Buffer</Tab>
              {/* Only where the Checkout step exists (RCC_OUTPUT_OVERRIDE) —
                  elsewhere the tab could only ever be empty. */}
              {isCheckoutEnabled && <Tab value="checkout-overrides">Checkout</Tab>}
            </TabGroup>
          </Box>

          {/* Tab content. The V2 surfaces mirror the published-plan summary page
              so the review step and the page it publishes to look identical.
              There is only one curve to draw here — the plan under review — so
              the V2 chart gets rccSeries and no overrideSeries. The V2 table
              reads shiftSummary from the store and owns its own category
              tabs, which is why ProcessPathTabs is V1-only. */}
          {activeReviewTab === "shift-plan" && (isPublishedShiftPlanV2Enabled ? (
            <>
              <MetricsSectionV2 />

              <Row widths={["fill", 440]} spacing="400" alignmentVertical="stretch">
                <Box type="outline" spacingInset="400">
                  <Column spacing="300">
                    <Heading type="h500" level={2}>
                      RCC Recommended Volume
                    </Heading>
                    <ChartV2
                      rccSeries={volumeSeries.projected}
                      actualSeries={volumeSeries.actual}
                    />
                  </Column>
                </Box>
                <Box type="outline" spacingInset="400">
                  <ShiftTotalsSectionV2 />
                </Box>
              </Row>

              <ShiftDataTableV2 />
            </>
          ) : (
            <>
          <MetricsSection />

          <Box className="timeline">
            <Box type="outline" className="graph">
              <Heading type="h500" level={1}>
                RCC Recommended Volume
              </Heading>
              <Chart />
            </Box>
            <ShiftTotalsSection />

          </Box>

          <Box>
            <ProcessPathTabs
              shiftSummary={shiftPlanOutput.shiftSummary}
              onTabChange={handleTabChange}
            />
          </Box>

          <ShiftDataTable selectedCategory={selectedCategory} />
            </>
          ))}

          {activeReviewTab === "rates-roster" && (
            <RosterAndRates readOnly={true} forceHideLoader={true} />
          )}

          {activeReviewTab === "volume-buffer" && (
            <VolumesAndWipContainer readOnly={true} />
          )}

          {activeReviewTab === "checkout-overrides" && isCheckoutEnabled && (
            <CheckoutOverridesView planId={planId ?? undefined} />
          )}

          <Footer
            nextButtonDisplay={isPublishing ? "Publishing..." : "Publish"}
            onNext={handlePublish}
            showClearOverrides={false}
            disabled={isPublishing}
          />
        </>
      ) : (
        <div>No review data available</div>
      )}
    </Box>
  );
};

export default ReviewAndPublish;

import './index.scss';